/**
 * Facebook Pixel bootstrap — browser-side companion to the server-side
 * Conversions API (CAPI) in `backend/app/services/analytics_service.py`.
 *
 * Usage:
 *   import { initFbPixel, fbTrack } from '../utils/fbPixel';
 *   initFbPixel();  // call once on LP mount
 *   fbTrack('Lead', { ... }, 'lead_123');  // event_id matches backend for dedupe
 *
 * We deliberately load the pixel ONLY on marketing surfaces (`/start`,
 * `/onboarding-payment`) so it never leaks to authenticated product pages.
 */

let _initialized = false;

/**
 * Load the FB Pixel script and fire the first PageView.
 * Idempotent — safe to call more than once per SPA session.
 */
export function initFbPixel() {
  if (_initialized) return;
  // The inline <head> snippet in public/index.html normally boots the pixel
  // long before React mounts, so PageView isn't gated behind the bundle. When
  // it already ran, this is a no-op — re-initializing would double-fire PageView.
  if (typeof window !== 'undefined' && window.__AVEN_PIXEL_BOOTED__) {
    _initialized = true;
    return;
  }
  const pixelId = process.env.REACT_APP_FB_PIXEL_ID;
  if (!pixelId) {
    // Analytics not configured — no-op so staging/dev don't throw.
    return;
  }

  // Standard FB Pixel bootstrap (from Meta's official snippet)
  // eslint-disable-next-line
  !(function (f, b, e, v, n, t, s) {
    if (f.fbq) return;
    n = f.fbq = function () {
      n.callMethod ? n.callMethod.apply(n, arguments) : n.queue.push(arguments);
    };
    if (!f._fbq) f._fbq = n;
    n.push = n;
    n.loaded = !0;
    n.version = '2.0';
    n.queue = [];
    t = b.createElement(e);
    t.async = !0;
    t.src = v;
    s = b.getElementsByTagName(e)[0];
    s.parentNode.insertBefore(t, s);
  })(window, document, 'script', 'https://connect.facebook.net/en_US/fbevents.js');

  try {
    window.fbq('init', pixelId);
    window.fbq('track', 'PageView');
    _initialized = true;
  } catch (e) {
    // Pixel blocked by an ad blocker — not fatal.
  }
}

/**
 * Fire a standard FB Pixel event with a deterministic event_id.
 *
 * The event_id MUST match what the backend sends via CAPI for the same
 * logical event so Meta dedupes the pair. Helpers:
 *   - Lead:       `lead_{user_id}`
 *   - Purchase:   `purchase_{user_id}_{subscription_id}` (or just user_id)
 *
 * @param {string} eventName - Standard FB event (e.g. 'Lead', 'Purchase')
 * @param {object} params - Event custom data (value, currency, etc.)
 * @param {string} eventId - Deterministic dedupe key
 */
export function fbTrack(eventName, params = {}, eventId = null) {
  try {
    if (!window.fbq) return;
    const options = eventId ? { eventID: eventId } : {};
    window.fbq('track', eventName, params, options);
  } catch (_) {}
}

/** Deterministic event_id helpers — mirror the backend. */
export function eventIdForLead(userId) {
  return `lead_${userId}`;
}

export function eventIdForPurchase(userId, subscriptionId) {
  if (subscriptionId) return `purchase_${userId}_${subscriptionId}`;
  return `purchase_${userId}`;
}

/**
 * Purchase dedupe key derived from the Stripe Checkout Session id.
 *
 * This is the identifier both sides share: the backend returns it from
 * /payments/create-embedded-checkout, and the webhook that fires the
 * server-side CAPI Purchase receives the same id. The page can't use
 * eventIdForPurchase() — it never learns user_id or subscription_id.
 *
 * Must match `event_id_for_checkout` in backend analytics_service.py.
 */
export function eventIdForCheckout(checkoutSessionId) {
  return `purchase_${checkoutSessionId}`;
}
